<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { format } from 'date-fns';
import Icon from 'dashboard/components-next/icon/Icon.vue';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  showEdit: {
    type: Boolean,
    default: false,
  },
  showAnalytics: {
    type: Boolean,
    default: false,
  },
  whatsappStats: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['edit', 'delete', 'analytics', 'results', 'repeat']);

const { t, locale } = useI18n();

const STATUS_COMPLETED = 'completed';
const STATUS_PROCESSING = 'processing';

const { formatMessage } = useMessageFormatter();

const isWhatsAppCampaign = computed(
  () => props.inbox?.channel_type === 'Channel::Whatsapp'
);

const isActive = computed(() =>
  props.isLiveChatType ? props.isEnabled : props.status !== STATUS_COMPLETED
);

// A processing campaign is already sending, so edits cannot apply to the run.
const isEditable = computed(
  () =>
    props.isLiveChatType ||
    (props.showEdit &&
      props.status !== STATUS_COMPLETED &&
      props.status !== STATUS_PROCESSING)
);

const statusTextColor = computed(() => ({
  'text-n-teal-11': isActive.value,
  'text-n-slate-12': !isActive.value,
}));

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (isWhatsAppCampaign.value) {
    const total = props.whatsappStats?.total || 0;
    const completed = props.whatsappStats?.completed || 0;
    const isDone = total > 0 && completed >= total;
    return isDone
      ? t('CAMPAIGN.WHATSAPP.CARD.STATUS.COMPLETED')
      : t('CAMPAIGN.WHATSAPP.CARD.STATUS.SENDING');
  }

  return props.status === STATUS_COMPLETED
    ? t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED')
    : t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const {
    medium,
    channel_type: type,
    voice_enabled: voiceEnabled,
  } = props.inbox;
  return getInboxIconByType(type, medium, 'fill', voiceEnabled);
});

const scheduledAtMs = computed(() => {
  const v = Number(props.scheduledAt || 0);
  // Backend sends unix seconds for one-off campaigns.
  return v > 0 ? v * 1000 : 0;
});

const scheduledAtLabel = computed(() => {
  if (!scheduledAtMs.value) return '';

  const currentLocale = String(locale.value || '').toLowerCase();
  const isPt =
    currentLocale === 'pt' ||
    currentLocale === 'pt-br' ||
    currentLocale === 'pt_br';

  // For Brazilian Portuguese, prefer numeric format (dd/MM/yyyy 24h).
  return isPt
    ? format(new Date(scheduledAtMs.value), 'dd/MM/yyyy HH:mm')
    : format(new Date(scheduledAtMs.value), 'MMM d, h:mm a');
});

const whatsappProgress = computed(() => {
  const total = props.whatsappStats?.total || 0;
  const percent = props.whatsappStats?.percent || 0;
  const sent = props.whatsappStats?.sent || 0;
  const failed = props.whatsappStats?.failed || 0;
  const queued = props.whatsappStats?.queued || 0;

  return { total, percent, sent, failed, queued };
});

const PROGRESS_WIDTH_CLASSES = {
  0: 'w-[0%]',
  5: 'w-[5%]',
  10: 'w-[10%]',
  15: 'w-[15%]',
  20: 'w-[20%]',
  25: 'w-[25%]',
  30: 'w-[30%]',
  35: 'w-[35%]',
  40: 'w-[40%]',
  45: 'w-[45%]',
  50: 'w-[50%]',
  55: 'w-[55%]',
  60: 'w-[60%]',
  65: 'w-[65%]',
  70: 'w-[70%]',
  75: 'w-[75%]',
  80: 'w-[80%]',
  85: 'w-[85%]',
  90: 'w-[90%]',
  95: 'w-[95%]',
  100: 'w-[100%]',
};

const progressWidthClass = computed(() => {
  const bounded = Math.min(
    100,
    Math.max(0, Number(whatsappProgress.value.percent || 0))
  );
  const rounded = Math.round(bounded / 5) * 5;
  return PROGRESS_WIDTH_CLASSES[rounded] || PROGRESS_WIDTH_CLASSES[0];
});
</script>

<template>
  <CardLayout layout="row">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <SMSCampaignDetails
          v-else-if="!isWhatsAppCampaign"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAtMs"
        />
        <template v-else>
          <span class="flex-shrink-0 text-sm text-n-slate-11 whitespace-nowrap">
            {{ t('CAMPAIGN.WHATSAPP.CARD.CAMPAIGN_DETAILS.SENT_FROM') }}
          </span>
          <div class="flex items-center gap-1.5 flex-shrink-0">
            <Icon
              :icon="inboxIcon"
              class="flex-shrink-0 text-n-slate-12 size-3"
            />
            <span class="text-sm font-medium text-n-slate-12">
              {{ inboxName }}
            </span>
          </div>
          <span class="flex-shrink-0 text-sm text-n-slate-11 whitespace-nowrap">
            {{ t('CAMPAIGN.WHATSAPP.CARD.CAMPAIGN_DETAILS.ON') }}
          </span>
          <span class="flex-1 text-sm font-medium truncate text-n-slate-12">
            {{ scheduledAtLabel }}
          </span>
        </template>
      </div>

      <div
        v-if="isWhatsAppCampaign && whatsappProgress.total > 0"
        class="w-full"
      >
        <div
          class="flex items-center justify-between text-xs text-n-slate-11 mb-1"
        >
          <span>
            {{
              t('CAMPAIGN.WHATSAPP.CARD.PROGRESS.TEXT', {
                sent: whatsappProgress.sent,
                total: whatsappProgress.total,
              })
            }}
          </span>
          <span>
            {{
              t('CAMPAIGN.WHATSAPP.CARD.PROGRESS.FAILED', {
                failed: whatsappProgress.failed,
              })
            }}
          </span>
        </div>
        <div class="h-2 w-full rounded-full bg-n-alpha-2 overflow-hidden">
          <div
            class="h-full rounded-full bg-n-teal-10 transition-all duration-200"
            :class="progressWidthClass"
          />
        </div>
      </div>
    </div>
    <div class="flex items-center justify-end w-20 gap-2">
      <Button
        v-if="showAnalytics"
        v-tooltip.top="t('CAMPAIGN.WHATSAPP.CARD.ANALYTICS')"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-chart-no-axes-column"
        :title="t('CAMPAIGN.WHATSAPP.CARD.ANALYTICS')"
        @click="emit('analytics')"
      />
      <Button
        v-if="isEditable"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-sliders-vertical"
        @click="emit('edit')"
      />
      <Button
        v-if="isWhatsAppCampaign"
        v-tooltip.top="t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.RESULTS_TOOLTIP')"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-bar-chart-3"
        @click="emit('results')"
      />
      <Button
        v-if="isWhatsAppCampaign"
        v-tooltip.top="t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.REPEAT_TOOLTIP')"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-rotate-cw"
        @click="emit('repeat')"
      />
      <Button
        v-tooltip.top="
          isWhatsAppCampaign
            ? t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.DELETE_TOOLTIP')
            : null
        "
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        @click="emit('delete')"
      />
    </div>
  </CardLayout>
</template>
